import Axios from 'axios';
import { walletInfoRC, isCashierCounterTokenWalletBrowserTabExpireShouldClose } from '@/utils/wallet/data';
import { getUrlParams } from '@/utils/base';
import { logoutToLogin, logoutCashierWebWalletCloseTabAfterExpire } from '@/utils/wallet';

// 默认 axios 实例请求配置
const configDefault = {
  timeout: 0,
  baseURL: '/api',
};

/** code、msg 均未返回时才兜底为未知异常【520】；任一有值则原样拼接（如 nginx 200 无业务体） */
const formatWalletErrToast = (msg, code) => {
  const hasMsg = msg != null && String(msg).trim() !== '';
  const hasCode = code != null && code !== '';
  if (!hasMsg && !hasCode) {
    return { msg: '未知异常', code: 520, text: '未知异常【520】' };
  }
  return { msg, code, text: `${msg}【${code}】` };
};

class Http {
  constructor(config) {
    Http.axiosConfigDefault = config;
    Http.axiosInstance = Axios.create(config);
    this.httpInterceptorsRequest();
    this.httpInterceptorsResponse();
  }
  // 当前实例
  static axiosInstance;
  // 请求配置
  static axiosConfigDefault;

  // 请求拦截
  httpInterceptorsRequest() {
    Http.axiosInstance.interceptors.request.use(
      config => {
        const snap = config.cashierBootstrapHeadersSnapshot;
        if (snap && typeof snap === 'object') {
          let { mchId, sourceMemberId, accessToken, walletMemberId } = snap;
          if (!accessToken) {
            const urlParams = getUrlParams();
            if (urlParams?.accessToken) accessToken = urlParams.accessToken;
          }
          if (accessToken && !config.ignoreToken) {
            config.headers['Authorization'] = `Bearer ${accessToken}`;
            config.headers['Accept-Language'] = 'zh-CN';
          }
          config.headers['Merchant'] = mchId;
          config.headers['SourceMemberId'] = sourceMemberId;
          if (walletMemberId != null && walletMemberId !== '') {
            config.headers['WalletMemberId'] = walletMemberId;
          }
          if (config.method === 'get') {
            if (config.params && config.contentType) {
              config.headers['Content-Type'] = config.contentType;
            }
          } else if (!config.iSupload) {
            config.headers['Content-Type'] = config.contentType || 'application/json';
          }
          return config;
        }

        let { accessToken, mchId, sourceMemberId, walletMemberId } = walletInfoRC() || {};
        // 68内部账号登录获取登录信息 使用URL的accessToken
        if (!accessToken) {
          const urlParams = getUrlParams();

          if (urlParams && urlParams.accessToken) {
            accessToken = urlParams.accessToken;
          }
        }
        // 发送请求前，可在此携带 token
        // 如果设置了 ignoreToken: true，则跳过添加 token（用于登录、注册等接口）
        if (accessToken && !config.ignoreToken) {
          config.headers['Authorization'] = `Bearer ${accessToken}`;
          // 如果请求url是买入订单列表，则写死 9991商户id
          //  config.headers['Merchant'] = config.url === '/order/front/buyOrder/v2/buyFromSellList' ? 9991 : mchId;
          config.headers['Merchant'] = mchId;
          config.headers['SourceMemberId'] = sourceMemberId;
          config.headers['WalletMemberId'] = walletMemberId;
          config.headers['Accept-Language'] = 'zh-CN';
        }

        if (config.method == 'get') {
          if (config.params) {
            if (config.contentType) {
              config.headers['Content-Type'] = config.contentType;
            }
          }
        }

        return config;
      },
      error => {
        window.$toast({
          text: error.message,
          type: 'error',
          duration: 1500,
        });
        return Promise.reject(error);
      }
    );
  }

  // 响应拦截
  httpInterceptorsResponse() {
    Http.axiosInstance.interceptors.response.use(
      response => {
        // 是否显示toast
        const { toast, returnErrMsg, needAllData, allowEmptySuccess } = response.config;
        // 是否需要返回所有数据
        if (needAllData) {
          const d = response.data;
          const u = response.config.url || '';
          if (d && d.code !== 0 && ![99996, 99997, 1110013, 3100001, 2100002, 2100003].includes(d.code) && u.includes('buyFromSellList')) {
            const { msg } = d;
            if (typeof msg === 'string') {
              response.data = { ...d, msg: `支付商家未接单，请更换通道尝试【${d.code}】` };
            }
          }
          return response.data;
        }

        let { code, msg, data } = response.data;
        // 判断请求是否成功 （code 200 请求成功）
        const isSuccess = (data || data === 0) && Reflect.has(response.data, 'code') && code === 0;
        if (isSuccess) {
          return data;
        }

        // 仅显式 allowEmptySuccess 的接口，避免全钱包 data 为 null 时与 code0 的语义被一律当成功
        if (allowEmptySuccess && Reflect.has(response.data, 'code') && code === 0) {
          return data;
        }

        // 弹出错误提示
        if (toast) {
          window.$toast({
            text: formatWalletErrToast(msg, code).text,
            type: 'error',
            duration: 1500,
          });
        }

        // 如果登录过期 || 账户被禁用 并且不为嵌入 则到登录页面
        if (code === 6102 || code === 6326) {
          // 是否为嵌入式钱包
          const isWalletEmbedded = location.pathname.indexOf('/wallet-embedded') === 0;

          // 如果为嵌入式钱包，则不跳转到登录页面, 并通知返回
          if (isWalletEmbedded) {
            window.$toast({
              text: `${msg}【${code}】`,
              type: 'error',
              duration: -1,
              showMask: true,
            });

            setTimeout(() => {
              document.location = 'postback://type=back@value=loginTimeout';
            }, 2000);
            return false;
          }

          // 是否为外置账户
          const isExternalAccount = localStorage.getItem('isExternalAccount') === '1';

          // 如果不是外置账户，则不跳转到登录页面
          if (!isExternalAccount) {
            window.$toast({
              text: `${msg}【${code}】`,
              type: 'error',
              duration: -1,
              showMask: true,
            });

            // 2秒后关闭页面
            setTimeout(() => {
              window.close();
            }, 2000);
            return false;
          }

          window.$toast({
            text: `${msg}【${code}】`,
            type: 'error',
            duration: 1500,
          });

          const closeCashierBrowserTab = isCashierCounterTokenWalletBrowserTabExpireShouldClose();
          const delayMs = closeCashierBrowserTab ? 2000 : 1500;
          setTimeout(() => {
            if (closeCashierBrowserTab) {
              logoutCashierWebWalletCloseTabAfterExpire();
            } else {
              logoutToLogin();
            }
          }, delayMs);
        }

        // 是否返回错误信息
        if (returnErrMsg) {
          return formatWalletErrToast(msg, code).text;
        }

        return false;
      },
      error => {
        // HTTP 状态码
        const status = error.response?.status;

        if (status) {
          window.$toast({
            text: `请求异常【 ${status} 】`,
            type: 'error',
            duration: 1500,
          });
        }

        return Promise.reject(error);
      }
    );
  }

  // 通用请求函数
  request(paramConfig) {
    const config = { ...Http.axiosConfigDefault, ...paramConfig };
    return new Promise((resolve, reject) => {
      Http.axiosInstance
        .request(config)
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  // 设置超时处理
  setupTimeoutHandler() {
    Http.axiosInstance.interceptors.response.use(
      response => response,
      error => {
        if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
          // 超时时返回 null
          return Promise.resolve(false);
        }
        return Promise.reject(error);
      }
    );
  }
}

export const http = new Http(configDefault);

// 有超时时间的请求
export const httpWithTimeout = new Http({
  ...configDefault,
  timeout: 10000,
});

// 为超时请求添加超时处理
httpWithTimeout.setupTimeoutHandler();
